import React, { useState } from 'react';

import { Provider, useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';

import { Store } from '@redux/store';
import { statusColorObj } from '@themes';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';

import CustomTooltip from '../shared/atoms/CustomTooltip';
import Spinner from '../shared/atoms/Spinner';
import * as S from './style';
import dotImg from '../../assets/dot.svg';

const MapIconWrapper = props => (
  <Provider store={Store}>
    <MapIcon {...props} />
  </Provider>
);

const Loading = () => {
  const theme = useSelector(store => store.theme.industry);
  const industries = useSelector(store => store.industriesState);
  return (
    <ThemeProvider theme={{ industry: theme }} industries={{ industries }}>
      <Spinner theme={{ industry: '#ffffff' }} />
    </ThemeProvider>
  );
};

const Fallback = () => <span>Invalid URL!</span>;

const maxRetries = 3;
const retryDelay = 1000; // 1 second delay

const MapIcon = ({
  id,
  src,
  name,
  status,
  dotIcon,
  isOverlapping,
  cursorStyle,
  customStyle,
  overrideSize,
  onClick,
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [triggerFetch, setTriggerFetch] = useState(Date.now());

  const getUrl = () => {
    return retryCount > 0 ? `${src}?cacheBust=${retryCount}` : src;
  };

  const handleError = error => {
    console.error(error);
    if (retryCount < maxRetries) {
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setTriggerFetch(Date.now());
      }, retryDelay);
    } else {
      console.error('SVG failed to load after maximum retries.');
    }
  };

  const renderContent = () => (
    <S.MapIcon
      $dotIcon={dotIcon}
      $overrideSize={overrideSize}
      $cursorStyle={cursorStyle}
      $status={status || false}
      style={customStyle}
      onClick={onClick}
    >
      <div className={status}>
        <ReactSVG
          key={triggerFetch}
          id={id}
          src={dotIcon ? dotImg : getUrl()}
          onError={handleError}
          evalScripts="always"
          fallback={Fallback}
          loading={Loading}
          renumerateIRIElements={false}
        />
        {!isOverlapping && <h5>{name}</h5>}
      </div>
    </S.MapIcon>
  );

  return isOverlapping ? (
    <CustomTooltip
      title={name}
      leaveDelay={0}
      componentsProps={{
        tooltip: {
          sx: {
            color: 'white',
            backgroundColor: statusColorObj[status],
            font: "12px/20px 'Helvetica Neue', Arial, Helvetica, sans-serif",
            fontSize: '1.25rem',
            fontWeight: 'bold',
          },
        },
        arrow: {
          sx: {
            '&::before': {
              backgroundColor: statusColorObj[status],
            },
          },
        },
      }}
    >
      {renderContent()}
    </CustomTooltip>
  ) : (
    renderContent()
  );
};

MapIcon.defaultProps = {
  status: 'default',
  dotIcon: false,
  isOverlapping: false,
  name: '',
  cursorStyle: 'pointer',
  customStyle: null,
  overrideSize: null,
};

MapIcon.propTypes = {
  id: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  name: PropTypes.string,
  dotIcon: PropTypes.bool,
  isOverlapping: PropTypes.bool,
  status: PropTypes.string,
  cursorStyle: PropTypes.string,
  customStyle: PropTypes.object,
  overrideSize: PropTypes.string,
  onClick: PropTypes.func,
};

export default MapIconWrapper;
